import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, RotateCcw } from 'lucide-react';
import { getSessionId, resetSession, isLeadSubmitted } from '../lib/chatSession';
import { ProductCards, CompareTable, ImageGrid } from './ChatWidgets';
import LeadCaptureCard from './LeadCaptureCard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const WELCOME = {
  role: 'assistant',
  content: "Welcome. I'm the MODULAROOM concierge — grounded in our official catalogue. Tell me about your project, and I can recommend the right suite, compare models, or walk you through our process.",
  structured: null,
};

const QUICK_STARTS = [
  'Recommend a suite for a beachfront resort',
  'Compare CS31 and CS33',
  'What is the production lead time?',
  'Best model for a private office',
];

function MessageBubble({ msg, dark }) {
  const isUser = msg.role === 'user';
  const s = msg.structured || {};
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.role}`}>
      <div className={`${isUser ? 'max-w-[85%]' : 'max-w-full w-full'}`}>
        {!isUser && (
          <div className={`text-[10px] tracking-[0.28em] uppercase ${dark ? 'text-[#C7A36A]' : 'text-[#C7A36A]'} mb-2`}>Concierge</div>
        )}
        <div className={`whitespace-pre-wrap text-sm leading-relaxed ${
          isUser
            ? (dark ? 'bg-white text-[#111] rounded-2xl rounded-tr-md px-4 py-3' : 'bg-[#111] text-white rounded-2xl rounded-tr-md px-4 py-3')
            : (dark ? 'text-white/90' : 'text-[#111]')
        }`}>
          {msg.content}
        </div>
        {!isUser && s.cards?.length > 0 && <ProductCards slugs={s.cards} dark={dark} />}
        {!isUser && s.compare?.length >= 2 && <CompareTable slugs={s.compare} dark={dark} />}
        {!isUser && s.images?.length > 0 && <ImageGrid keys={s.images} />}
      </div>
    </div>
  );
}

export default function Concierge({ variant = 'floating' }) {
  const isFloating = variant === 'floating';
  const [open, setOpen] = useState(!isFloating); // full page starts open
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [leadShown, setLeadShown] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || sending) return;
    setInput('');
    const userMsg = { role: 'user', content: clean, structured: null, id: `u-${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`, { session_id: sid.current, message: clean });
      sid.current = data.session_id;
      const m = data.message;
      setMessages((prev) => [...prev, { role: 'assistant', content: m.content, structured: m.structured, id: m.id }]);
      if (m.structured?.lead_prompt && !isLeadSubmitted()) setLeadShown(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'The concierge is briefly unavailable. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: typeof detail === 'string' ? detail : 'Try again shortly.', structured: null, id: `e-${Date.now()}` }]);
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    sid.current = resetSession();
    setMessages([WELCOME]);
    setLeadShown(false);
  };

  const lastAssistantSlug = [...messages].reverse().find((m) => m.role === 'assistant' && m.structured?.cards?.length)?.structured?.cards?.[0] || '';

  if (isFloating && !open) {
    return (
      <button
        data-testid="concierge-toggle"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-5 right-5 md:bottom-8 md:right-8 group inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-5 py-3.5 shadow-[0_8px_40px_rgba(0,0,0,0.25)] hover:bg-[#C7A36A] hover:text-white transition-colors"
      >
        <MessageCircle size={16} />
        <span className="text-[12px] tracking-[0.2em] uppercase">Ask MODULAROOM</span>
      </button>
    );
  }

  // FLOATING PANEL LAYOUT
  if (isFloating) {
    return (
      <div className="fixed z-40 bottom-0 right-0 md:bottom-6 md:right-6 w-full md:w-[440px] h-[92vh] md:h-[76vh] max-h-[720px] flex flex-col rounded-none md:rounded-2xl overflow-hidden bg-[#111] text-white border-t md:border border-white/10 shadow-2xl" data-testid="concierge-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C7A36A]">MODULAROOM</div>
            <div className="font-display text-lg tracking-tight">Concierge</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="concierge-reset" onClick={handleReset} className="p-2 rounded-full hover:bg-white/10" title="New chat"><RotateCcw size={16}/></button>
            <button data-testid="concierge-close" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10" title="Close"><X size={18}/></button>
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-5" data-testid="concierge-messages">
          {messages.map((m) => <MessageBubble key={m.id || `${m.role}-${m.content.slice(0,20)}`} msg={m} dark />)}
          {sending && <div className="text-white/50 text-sm italic">Concierge is thinking…</div>}
          {leadShown && <LeadCaptureCard dark defaultInterest={lastAssistantSlug} />}
          {messages.length === 1 && (
            <div className="pt-2 flex flex-wrap gap-2" data-testid="concierge-quickstarts">
              {QUICK_STARTS.map((q) => (
                <button key={q} onClick={() => send(q)} className="rounded-full border border-white/15 hover:border-[#C7A36A] hover:text-[#C7A36A] px-3 py-1.5 text-[11px] tracking-wide transition-colors">{q}</button>
              ))}
            </div>
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/10 flex items-center gap-2" data-testid="concierge-form">
          <input
            data-testid="concierge-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a model, an application, timeline…"
            className="flex-1 bg-transparent outline-none border border-white/10 focus:border-[#C7A36A] transition-colors rounded-full px-4 py-2.5 text-sm placeholder:text-white/35"
          />
          <button data-testid="concierge-send" type="submit" disabled={sending || !input.trim()} className="rounded-full bg-white text-[#111] w-10 h-10 flex items-center justify-center hover:bg-[#C7A36A] hover:text-white transition-colors disabled:opacity-40"><Send size={15}/></button>
        </form>
        <div className="px-4 pb-3 -mt-2 text-[10px] text-white/35 tracking-wide">
          Grounded in the official MODULAROOM catalogue
        </div>
      </div>
    );
  }

  // FULL PAGE LAYOUT (dark)
  return (
    <div className="bg-[#111] text-white min-h-[100svh]" data-testid="concierge-page">
      <div className="max-w-[980px] mx-auto px-6 md:px-12 pt-40 pb-16">
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#C7A36A]">AI Design & Product Consultant</div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 tracking-tight leading-[0.98]">Concierge.</h1>
        <p className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed">
          A private assistant grounded in the official MODULAROOM catalogue. Ask for a recommendation, compare models, or plan your project — all in one place.
        </p>
      </div>
      <div className="max-w-[980px] mx-auto px-6 md:px-12 pb-40">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] flex flex-col min-h-[62vh] max-h-[80vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6" data-testid="assistant-messages">
            {messages.map((m) => <MessageBubble key={m.id || `${m.role}-${m.content.slice(0,20)}`} msg={m} dark />)}
            {sending && <div className="text-white/50 text-sm italic">Concierge is thinking…</div>}
            {leadShown && <LeadCaptureCard dark defaultInterest={lastAssistantSlug} />}
            {messages.length === 1 && (
              <div className="pt-2 flex flex-wrap gap-2" data-testid="assistant-quickstarts">
                {QUICK_STARTS.map((q) => (
                  <button key={q} onClick={() => send(q)} className="rounded-full border border-white/15 hover:border-[#C7A36A] hover:text-[#C7A36A] px-4 py-2 text-[12px] tracking-wide transition-colors">{q}</button>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 md:p-5 border-t border-white/10 flex items-center gap-2" data-testid="assistant-form">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message the concierge…"
              className="flex-1 bg-transparent outline-none border border-white/10 focus:border-[#C7A36A] transition-colors rounded-full px-5 py-3 text-sm placeholder:text-white/35"
            />
            <button data-testid="assistant-send" type="submit" disabled={sending || !input.trim()} className="rounded-full bg-white text-[#111] w-11 h-11 flex items-center justify-center hover:bg-[#C7A36A] hover:text-white transition-colors disabled:opacity-40"><Send size={16}/></button>
            <button type="button" data-testid="assistant-reset" onClick={handleReset} className="rounded-full border border-white/15 hover:border-[#C7A36A] hover:text-[#C7A36A] px-4 py-3 text-[11px] tracking-[0.2em] uppercase transition-colors">New</button>
          </form>
        </div>
        <div className="mt-6 text-[11px] text-white/40">
          The concierge only speaks about MODULAROOM. It will never invent specifications, pricing or compliance information — always confirm final details via a consultation.
        </div>
      </div>
    </div>
  );
}
